/**
 * The console URLs that moved, and where they are now.
 *
 * The rule: the first part of a URL is the sidebar section, and the second part is the page. Some
 * pages did not follow it, so the URL and the breadcrumb named a different section from the
 * sidebar. Operators have bookmarks of the old URLs, so `__root.tsx` sends them to the new page.
 *
 * Remove an entry one release after the move.
 */

/** The path and every path under it. A more specific path comes before its parent. */
const PREFIXES: readonly (readonly [string, string])[] = [
  ['/registrations', '/ticketing/registrations'],
  ['/settings/ticket-types', '/ticketing/ticket-types'],
  ['/settings/vouchers', '/ticketing/vouchers'],
  ['/settings/registration-form', '/ticketing/registration-form'],
  ['/onsite', '/ticketing/check-in'],
  ['/exhibition/service-catalog', '/exhibition/service-items'],
  ['/exhibitor/floor', '/exhibition/floor'],
  ['/exhibitor', '/exhibition/exhibitors'],
  ['/content/media', '/cms/media'],
  ['/content', '/cms/content'],
  ['/programme', '/program'],
]

/** Only the section root. It showed the first page, and the other pages under it did not move. */
const ROOTS: ReadonlyMap<string, string> = new Map([
  ['/program', '/program/sessions'],
  ['/billing', '/billing/invoices'],
])

/** One page that a new page replaced. The pages beside it did not move. */
const PAGES: ReadonlyMap<string, string> = new Map([
  ['/ticketing/registrations/new', '/ticketing/desk'],
])

/** The new path for an old path, or null when the path did not move. */
export function movedPath(pathname: string): string | null {
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname
  const page = PAGES.get(path)
  if (page !== undefined) return page
  const root = ROOTS.get(path)
  if (root !== undefined) return root
  for (const [from, to] of PREFIXES) {
    if (path === from || path.startsWith(`${from}/`)) return to + path.slice(from.length)
  }
  return null
}
